import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import {
  getAIClassificationJob,
  getAIClassifications,
  rerunAIClassification,
  resumeAIClassification,
  signAIClassificationReport,
  startOrContinueAIClassification
} from '../services/AIClassificationService';
import {
  FollowUpAnswer,
  GetAIClassificationsParams,
  RerunAIClassificationBody
} from '../types/AIClassification.types';
import {
  fetchClassificationsFailure,
  fetchClassificationsStart,
  fetchClassificationsSuccess,
  pollClassificationFailure,
  pollClassificationStart,
  pollClassificationSuccess,
  rerunClassificationFailure,
  rerunClassificationStart,
  rerunClassificationSuccess,
  resumeClassificationFailure,
  resumeClassificationStart,
  resumeClassificationSuccess,
  setFollowUpAnswers,
  signClassificationFailure,
  signClassificationStart,
  signClassificationSuccess,
  startClassificationFailure,
  startClassificationStart,
  startClassificationSuccess,
  submitFollowUpAnswerFailure,
  submitFollowUpAnswerStart,
  submitFollowUpAnswerSuccess
} from '../store/slices/aiPoweredSlice';
import { getApiErrorMessage, getApiErrorStatus } from '../utils/apiError';

type UseAIClassificationOptions = {
  pollIntervalMs?: number;
  enablePolling?: boolean;
};

const DEFAULT_POLL_INTERVAL_MS = 5000;

const getErrorPayload = (error: unknown, fallback: string) => ({
  message: getApiErrorMessage(error, fallback),
  status: getApiErrorStatus(error)
});

export const useAIClassification = (
  aiSystemId?: string,
  options: UseAIClassificationOptions = {}
) => {
  const { pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, enablePolling = true } =
    options;

  const dispatch = useAppDispatch();
  const aiPowered = useAppSelector((state) => state.aiPowered);
  const pollIntervalRef = useRef<number | null>(null);
  const pollingRequestInFlightRef = useRef(false);
  const lastPolledJobIdRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const fetchClassifications = useCallback(
    async (params?: GetAIClassificationsParams) => {
      if (!aiSystemId) return null;

      try {
        dispatch(fetchClassificationsStart());
        const response = await getAIClassifications(aiSystemId, params);
        dispatch(fetchClassificationsSuccess(response));
        return response;
      } catch (error) {
        dispatch(
          fetchClassificationsFailure(
            getErrorPayload(error, 'Failed to get AI classifications')
          )
        );
        return null;
      }
    },
    [aiSystemId, dispatch]
  );

  const startClassification = useCallback(async () => {
    if (!aiSystemId) return null;

    try {
      dispatch(startClassificationStart(aiSystemId));
      const response = await startOrContinueAIClassification({
        aiSystemId,
        body: { regulation: 'EU_AI_ACT' }
      });
      dispatch(startClassificationSuccess(response));
      return response;
    } catch (error) {
      dispatch(
        startClassificationFailure(
          getErrorPayload(error, 'Failed to start AI classification')
        )
      );
      return null;
    }
  }, [aiSystemId, dispatch]);

  const pollClassification = useCallback(
    async (jobId = aiPowered.jobId) => {
      if (!aiSystemId || !jobId || pollingRequestInFlightRef.current) {
        return null;
      }

      try {
        pollingRequestInFlightRef.current = true;
        dispatch(pollClassificationStart());
        const response = await getAIClassificationJob(aiSystemId, jobId);
        dispatch(pollClassificationSuccess(response));
        return response;
      } catch (error) {
        dispatch(
          pollClassificationFailure(
            getErrorPayload(error, 'Failed to poll AI classification')
          )
        );
        return null;
      } finally {
        pollingRequestInFlightRef.current = false;
      }
    },
    [aiPowered.jobId, aiSystemId, dispatch]
  );

  const submitFollowUpAnswer = useCallback(
    async (answer: string) => {
      if (!aiSystemId || !aiPowered.currentJob?.followUpQuestion) return null;

      const followUpAnswer: FollowUpAnswer = {
        question: aiPowered.currentJob.followUpQuestion,
        answer,
        options: aiPowered.currentJob.followUpOptions ?? undefined
      };

      const nextFollowUpAnswers = [
        ...aiPowered.followUpAnswers,
        followUpAnswer
      ];

      try {
        dispatch(submitFollowUpAnswerStart());
        const response = await startOrContinueAIClassification({
          aiSystemId,
          body: {
            regulation: 'EU_AI_ACT',
            followUpAnswers: nextFollowUpAnswers
          }
        });
        dispatch(setFollowUpAnswers(nextFollowUpAnswers));
        dispatch(submitFollowUpAnswerSuccess(response));
        return response;
      } catch (error) {
        dispatch(
          submitFollowUpAnswerFailure(
            getErrorPayload(error, 'Failed to submit follow-up answer')
          )
        );
        return null;
      }
    },
    [aiPowered.currentJob, aiPowered.followUpAnswers, aiSystemId, dispatch]
  );

  const rerunClassification = useCallback(
    async (body: RerunAIClassificationBody) => {
      if (!aiSystemId) return null;

      try {
        dispatch(
          rerunClassificationStart(
            body.description !== undefined ? 'description' : 'answers'
          )
        );

        const response = await rerunAIClassification({ aiSystemId, body });
        dispatch(rerunClassificationSuccess(response));
        if (
          body.followUpAnswers &&
          body.editedAtIndex !== undefined &&
          body.editedAtIndex >= 0
        ) {
          dispatch(
            setFollowUpAnswers(
              body.followUpAnswers.slice(0, body.editedAtIndex + 1)
            )
          );
        }
        return response;
      } catch (error) {
        dispatch(
          rerunClassificationFailure(
            getErrorPayload(error, 'Failed to rerun AI classification')
          )
        );
        return null;
      }
    },
    [aiSystemId, dispatch]
  );

  const resumeClassification = useCallback(
    async (jobId = aiPowered.jobId) => {
      if (!aiSystemId || !jobId) return null;

      try {
        dispatch(resumeClassificationStart());
        const response = await resumeAIClassification(aiSystemId, jobId);
        dispatch(resumeClassificationSuccess(response));
        return response;
      } catch (error) {
        dispatch(
          resumeClassificationFailure(
            getErrorPayload(error, 'Failed to resume AI classification')
          )
        );
        return null;
      }
    },
    [aiPowered.jobId, aiSystemId, dispatch]
  );

  const signClassification = useCallback(
    async (jobId = aiPowered.jobId) => {
      if (!aiSystemId || !jobId) return null;

      try {
        dispatch(signClassificationStart());
        const response = await signAIClassificationReport(aiSystemId, jobId);
        dispatch(signClassificationSuccess(response));
        return response;
      } catch (error) {
        dispatch(
          signClassificationFailure(
            getErrorPayload(error, 'Failed to sign AI classification report')
          )
        );
        return null;
      }
    },
    [aiPowered.jobId, aiSystemId, dispatch]
  );

  useEffect(() => {
    if (
      !aiSystemId ||
      !aiPowered.jobId ||
      !enablePolling ||
      (aiPowered.aiSystemId !== null && aiPowered.aiSystemId !== aiSystemId)
    ) {
      stopPolling();
      lastPolledJobIdRef.current = null;
      return;
    }

    const shouldPoll =
      !aiPowered.currentJob ||
      aiPowered.currentJob.status === 'PENDING' ||
      aiPowered.currentJob.status === 'PROCESSING' ||
      aiPowered.currentJob.processState === 'IN_PROGRESS';

    if (!shouldPoll) {
      stopPolling();
      lastPolledJobIdRef.current = null;
      return;
    }

    if (lastPolledJobIdRef.current !== aiPowered.jobId) {
      lastPolledJobIdRef.current = aiPowered.jobId;
      pollClassification(aiPowered.jobId);
    }

    if (pollIntervalRef.current === null) {
      pollIntervalRef.current = window.setInterval(() => {
        pollClassification(aiPowered.jobId);
      }, pollIntervalMs);
    }

    return stopPolling;
  }, [
    aiPowered.currentJob?.processState,
    aiPowered.currentJob?.status,
    aiPowered.aiSystemId,
    aiPowered.jobId,
    enablePolling,
    aiSystemId,
    pollClassification,
    pollIntervalMs,
    stopPolling
  ]);

  return {
    aiPowered,
    fetchClassifications,
    startClassification,
    pollClassification,
    submitFollowUpAnswer,
    rerunClassification,
    resumeClassification,
    signClassification,
    stopPolling
  };
};
